import gsap from "gsap";
import { SplitText } from "gsap/SplitText";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { pageReady } from "../../utils/page-ready";

gsap.registerPlugin(SplitText, ScrollTrigger);

const DURATION = 1.2;
const EASE = "expo.out";
const GROUP_OVERLAP = "-=0.8";
const GROUP_STAGGER = 0.2;
const GROUP_DELAY = 0.2;

const glitchConfig = {
  countMin: 8,              // min number of squares flickering per burst
  countMax: 50,             // max number of squares flickering per burst
  flickerDuration: 0.01,    // how long a square stays hidden (seconds)
  repeatMin: 1,             // min number of on/off cycles per square per burst
  repeatMax: 2,             // max number of on/off cycles per square per burst
  slowReappearChance: 0.8,  // probability (0–1) a square reappears slowly
  reappearDelayMin: 0.2,    // min delay before a slow square reappears (seconds)
  reappearDelayMax: 1,    // max delay before a slow square reappears (seconds)
  burstIntervalMin: 3,      // min pause between two glitch bursts (seconds)
  burstIntervalMax: 6,     // max pause between two glitch bursts (seconds)
  initialDelayMax: 2,       // max random delay before the first burst (seconds)
  fullGlitch: 0.1,          // probability (0–1) of triggering a full glitch
  fullStagger: 0.01,        // delay between each square disappearing (seconds)
  killReappearMin: 1,       // min time a hovered square stays hidden (seconds)
  killReappearMax: 4,       // max time a hovered square stays hidden (seconds)
  hoverRadius: 10,          // cursor reach around the pointer (pixels)
  fullHold: 0.4,            // pause with all squares hidden before reappearing (seconds)
};

const rand = (min, max) => min + Math.random() * (max - min);
const randInt = (min, max) => min + Math.floor(Math.random() * (max - min + 1));

export function startSvgGlitch(el, initialDelay = 0) {
  const paths = [...el.querySelectorAll("path")];
  if (!paths.length) return;

  // Squares "killed" by the cursor are hidden for a random time, then pop back.
  // The ambient glitch skips them meanwhile.
  const killed = new WeakSet();

  const kill = (path) => {
    if (killed.has(path)) return;
    killed.add(path);
    gsap.killTweensOf(path);
    gsap.set(path, { opacity: 0, pointerEvents: "none" });
    gsap.delayedCall(rand(glitchConfig.killReappearMin, glitchConfig.killReappearMax), () => {
      killed.delete(path);
      gsap.set(path, { opacity: 1, pointerEvents: "" });
    });
  };

  // Kills every square within hoverRadius of the segment travelled since the last
  // pointer event, so fast swipes skip nothing.
  let last = null;
  el.addEventListener("pointermove", (e) => {
    if (e.pointerType !== "mouse") return;
    const from = last ?? { x: e.clientX, y: e.clientY };
    last = { x: e.clientX, y: e.clientY };
    const dx = last.x - from.x;
    const dy = last.y - from.y;
    const lenSq = dx * dx + dy * dy;

    paths.forEach((path) => {
      if (killed.has(path)) return;
      const r = path.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      const t = lenSq ? Math.max(0, Math.min(1, ((cx - from.x) * dx + (cy - from.y) * dy) / lenSq)) : 0;
      const dist = Math.hypot(cx - (from.x + dx * t), cy - (from.y + dy * t));
      if (dist <= glitchConfig.hoverRadius + Math.max(r.width, r.height) / 2) kill(path);
    });
  });
  el.addEventListener("pointerleave", () => {
    last = null;
  });

  const fullGlitch = () => {
    const hideOrder = paths.filter((p) => !killed.has(p)).sort(() => Math.random() - 0.5);
    const showOrder = [...hideOrder];

    gsap.to(hideOrder, {
      opacity: 0,
      duration: glitchConfig.flickerDuration,
      ease: "steps(1)",
      stagger: glitchConfig.fullStagger,
      onComplete: () => {
        gsap.delayedCall(glitchConfig.fullHold, () => {
          gsap.to(showOrder.filter((p) => !killed.has(p)), {
            opacity: 1,
            duration: glitchConfig.flickerDuration,
            ease: "steps(1)",
            stagger: glitchConfig.fullStagger,
          });
        });
      },
    });
  };

  const glitch = () => {
    if (Math.random() < glitchConfig.fullGlitch) {
      fullGlitch();
    } else {
      const count = randInt(glitchConfig.countMin, glitchConfig.countMax);
      const shuffled = paths.filter((p) => !killed.has(p)).sort(() => Math.random() - 0.5).slice(0, count);

      shuffled.forEach((path) => {
        const repeatDelay = Math.random() < glitchConfig.slowReappearChance
          ? rand(glitchConfig.reappearDelayMin, glitchConfig.reappearDelayMax)
          : glitchConfig.reappearDelayMin;
        gsap.to(path, {
          opacity: 0,
          duration: glitchConfig.flickerDuration,
          ease: "steps(1)",
          yoyo: true,
          repeat: randInt(glitchConfig.repeatMin, glitchConfig.repeatMax),
          repeatDelay,
          onComplete: () => {
            if (!killed.has(path)) gsap.set(path, { opacity: 1 });
          },
        });
      });
    }

    gsap.delayedCall(rand(glitchConfig.burstIntervalMin, glitchConfig.burstIntervalMax), glitch);
  };

  gsap.delayedCall(initialDelay, glitch);
}

export default class Anim {
  static init() {
    // Fonts for the text splits, and the page uncovered (loader / transition overlay):
    // the text/fade entrances would otherwise finish before anyone sees them.
    Promise.all([document.fonts.ready, pageReady.ready]).then(() => {
      Anim.setup();
    }).catch(() => {
      document.body.classList.add("gsap-not-found");
    });
  }

  static setup() {
    const groups = new Map();
    Anim.setupTextMask(groups);
    Anim.setupFade(groups);
    Anim.setupGroups(groups);
    Anim.setupLetterReveal();
    Anim.setupSvgGlitch();
  }

  static setupTextMask(groups) {
    const els = [...document.querySelectorAll("[data-text-mask]")];
    if (!els.length) return;

    els.forEach((el) => {
      const splitType = el.dataset.split;
      const animType = el.dataset.textMaskAnim || "scroll";
      const groupName = el.dataset.animGroup;
      const fromY = el.dataset.textMaskY || "110%";
      const delay = parseFloat(el.dataset.animDelay) || 0;

      let targets;
      let triggerEl = el;
      if (splitType) {
        const split = new SplitText(el, { type: splitType, charsClass: 'char-mask', linesClass: 'line-mask', wordsClass: 'word-mask' });
        targets = split[splitType];
      } else {
        targets = [el];
      }
      const masks = targets.map(Anim.wrapInMask);
      if (!splitType) triggerEl = masks[0];

      if (animType === "manual") return;

      if (groupName) {
        if (!groups.has(groupName)) groups.set(groupName, []);
        groups.get(groupName).push({ el, triggerEl, targets, type: "text-mask", animType, fromY, delay, animStart: el.dataset.animStart });
        return;
      }

      const stagger = targets.length > 1 ? GROUP_STAGGER : 0;
      const vars = { y: "0%", duration: DURATION, ease: EASE, stagger };

      if (animType === "page") {
        gsap.fromTo(targets, { y: fromY }, { ...vars, delay: GROUP_DELAY + delay });
      } else {
        const start = el.dataset.animStart || "top 50%";
        const anim = gsap.fromTo(targets, { y: fromY }, { ...vars, delay, paused: true });
        ScrollTrigger.create({ trigger: triggerEl, start, onEnter: () => anim.play() });
      }
    });
  }

  static setupFade(groups) {
    const els = [...document.querySelectorAll("[data-fade-in]")];
    if (!els.length) return;

    els.forEach((el) => {
      const animType = el.dataset.fadeIn || "scroll";
      const delay = parseFloat(el.dataset.animDelay) || 0;
      const groupName = el.dataset.animGroup;

      el.style.opacity = "0";

      if (groupName) {
        if (!groups.has(groupName)) groups.set(groupName, []);
        groups.get(groupName).push({ el, targets: [el], type: "fade", animType, delay, animStart: el.dataset.animStart });
        return;
      }

      const anim = gsap.to(el, { opacity: 1, duration: DURATION, ease: EASE, delay, paused: true });

      if (animType === "page") {
        anim.play();
      } else {
        const start = el.dataset.animStart || "top 50%";
        ScrollTrigger.create({ trigger: el, start, onEnter: () => anim.play() });
      }
    });
  }

  static setupLetterReveal() {
    const els = [...document.querySelectorAll("[data-letter-reveal]")];
    if (!els.length) return;

    let prevTrigger = null;

    els.forEach((el) => {
      const start = el.dataset.animStart || "top 80%";
      const end = el.dataset.animEnd || "bottom 60%";
      const split = new SplitText(el, { type: "words,chars", charsClass: "char-reveal", wordsClass: "word-reveal" });
      const chained = el.hasAttribute("data-letter-reveal-after") && prevTrigger;

      gsap.set(split.chars, { opacity: 0.1 });

      let scrollTrigger = { trigger: el, start, end, scrub: true };
      if (chained) {
        // Start exactly where the previous reveal ends, keeping this element's own scroll length
        const prev = prevTrigger;
        const own = ScrollTrigger.create({ trigger: el, start, end });
        scrollTrigger = {
          trigger: el,
          start: () => prev.end,
          end: () => prev.end + (own.end - own.start),
          scrub: true,
        };
      }

      const tween = gsap.to(split.chars, {
        opacity: 1,
        stagger: 0.1,
        ease: "none",
        scrollTrigger,
      });
      prevTrigger = tween.scrollTrigger;
    });
  }

  static setupGroups(groups) {
    groups.forEach((entries) => {
      // Re-sort entries in DOM order
      entries.sort((a, b) => a.el.compareDocumentPosition(b.el) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1);

      const animType = entries[0].animType;
      const tl = Anim.buildGroupTimeline(entries);

      if (animType === "scroll") {
        const start = entries[0].animStart || "top 50%";
        tl.pause();
        ScrollTrigger.create({
          trigger: entries[0].triggerEl ?? entries[0].el,
          start,
          onEnter: () => tl.play(),
        });
      }
    });
  }

  static buildGroupTimeline(entries) {
    const tl = gsap.timeline({ delay: GROUP_DELAY });

    entries.forEach(({ type, targets, fromY, delay }, i) => {
      const base = i === 0 ? 0 : GROUP_OVERLAP;
      const pos = delay ? `${typeof base === "number" ? base : base}+=${delay}` : base;
      const stagger = targets.length > 1 ? GROUP_STAGGER : 0;

      if (type === "text-mask") {
        tl.fromTo(targets, { y: fromY }, { y: "0%", duration: DURATION, ease: EASE, stagger }, pos);
      } else {
        tl.fromTo(targets, { opacity: 0 }, { opacity: 1, duration: DURATION, ease: EASE }, pos);
      }
    });

    return tl;
  }

  static setupSvgGlitch() {
    const els = [...document.querySelectorAll("[data-svg]:not([data-svg-defer])")];
    if (els.length) {
      els.forEach((el) => startSvgGlitch(el, rand(0, glitchConfig.initialDelayMax)));
    }
  }

  static wrapInMask(el) {
    const mask = document.createElement("div");
    mask.classList.add("c-text-mask");
    el.parentNode.insertBefore(mask, el);
    mask.appendChild(el);
    el.style.opacity = "1";
    return mask;
  }
}
